import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarDays, Users2, Music2, Mic2, AudioLines, Sparkles, ArrowRight, CheckCircle2, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { PageHeader, StatusBadge, KeyBadge, BpmBadge } from "@/components/ui-kit";
import { Progress } from "@/components/ui/progress";

function fmtDate(d) {
  if (!d) return "—";
  try { return new Date(d + "T00:00:00").toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" }); }
  catch (e) { return d; }
}

function StatCard({ icon: Icon, label, value, sub, onClick, testId }) {
  return (
    <button onClick={onClick} data-testid={testId} className="wf-card rounded-2xl p-5 text-left transition-all hover:-translate-y-0.5">
      <div className="flex items-center justify-between mb-3">
        <div className="w-10 h-10 rounded-xl bg-[#E5C378]/12 flex items-center justify-center"><Icon className="w-5 h-5 text-[#E5C378]" /></div>
        <ArrowRight className="w-4 h-4 text-[#6E7D97]" />
      </div>
      <div className="font-display text-2xl font-bold text-[#F7F5F0]">{value}</div>
      <div className="text-xs text-[#8A97AC] mt-0.5">{label}</div>
      {sub && <div className="text-[11px] text-[#6E7D97] mt-1">{sub}</div>}
    </button>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const nav = useNavigate();
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);

  if (!d) return <div className="text-[#8A97AC]">Loading…</div>;
  const ns = d.next_service;
  const st = d.stats || {};
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <div>
      <PageHeader
        eyebrow={greet}
        title={`${greet}, ${d.greeting_name || "friend"}`}
        subtitle="Here's where your team stands for the next gathering."
      />

      {/* Next service hero */}
      {ns ? (
        <div className="bg-gradient-to-br from-[#121B2F] to-[#0A0F1D] border border-[#E5C378]/25 rounded-2xl p-6 sm:p-8 shadow-2xl relative overflow-hidden mb-6 wf-fade-up">
          <div className="absolute top-0 right-0 w-44 h-44 bg-[#E5C378]/10 rounded-full blur-3xl pointer-events-none" />
          <div className="relative flex flex-col lg:flex-row lg:items-center justify-between gap-6">
            <div className="flex-1">
              <div className="eyebrow mb-2">Next service</div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold text-white tracking-tight">{ns.title}</h2>
              <div className="flex flex-wrap gap-4 text-sm text-[#A0AEC0] mt-3">
                <span className="flex items-center gap-1.5"><CalendarDays className="w-4 h-4" /> {fmtDate(ns.date)}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4" /> {ns.time}</span>
                <StatusBadge status={ns.status} />
              </div>
              <div className="mt-5 max-w-md">
                <div className="flex items-center justify-between text-xs text-[#B8C1D1] mb-1.5">
                  <span>Preparation</span><span className="font-mono text-[#E5C378] font-bold">{st.preparation || 0}%</span>
                </div>
                <Progress value={st.preparation || 0} className="h-2 bg-[#080C14]" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 lg:w-72">
              <div className="bg-[#090D16] rounded-xl p-3 border border-white/5 text-center">
                <div className="font-display text-xl font-bold text-white">{st.confirmed}/{st.team_total}</div>
                <div className="text-[10px] text-[#8A97AC] mt-1">Confirmed</div>
              </div>
              <div className="bg-[#090D16] rounded-xl p-3 border border-white/5 text-center">
                <div className="font-display text-xl font-bold text-white">{st.songs}</div>
                <div className="text-[10px] text-[#8A97AC] mt-1">Songs</div>
              </div>
              <div className="bg-[#090D16] rounded-xl p-3 border border-white/5 text-center">
                <div className="font-display text-sm font-bold text-white">{st.rehearsal?.date ? "Thu" : "—"}</div>
                <div className="text-[10px] text-[#8A97AC] mt-1">{st.rehearsal?.time || "No rehearsal"}</div>
              </div>
            </div>
          </div>
          <button onClick={() => nav(`/services/${ns.id}`)} data-testid="open-next-service"
            className="relative mt-6 inline-flex items-center gap-2 px-5 py-2.5 rounded-xl wf-gold-btn font-semibold text-sm">
            Open service plan <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="wf-card rounded-2xl p-8 text-center mb-6">
          <p className="text-[#8A97AC]">No service scheduled. Create your next worship service.</p>
          <button onClick={() => nav("/services")} className="mt-3 px-5 py-2.5 rounded-xl wf-gold-btn font-semibold text-sm">Go to Services</button>
        </div>
      )}

      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users2} label="Active team members" value={d.totals.members} onClick={() => nav("/team")} testId="stat-team" />
        <StatCard icon={Music2} label="Songs in library" value={d.totals.songs} sub={`${d.totals.songs_no_charts} without charts`} onClick={() => nav("/songs")} testId="stat-songs" />
        <StatCard icon={CalendarDays} label="Upcoming services" value={d.totals.upcoming} onClick={() => nav("/services")} testId="stat-services" />
        <StatCard icon={AudioLines} label="Pad & Beat Studio" value="Open" onClick={() => nav("/studio")} testId="stat-studio" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* My assignments */}
        <div className="wf-card rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4"><Mic2 className="w-4 h-4 text-[#E5C378]" /><h3 className="font-display font-semibold text-[#F7F5F0]">My next assignments</h3></div>
          {d.my_assignments.length === 0 ? (
            <p className="text-sm text-[#6E7D97] py-6 text-center">You have no upcoming assignments.</p>
          ) : (
            <div className="space-y-2">
              {d.my_assignments.map((a) => (
                <button key={a.assignment_id} onClick={() => nav(`/services/${a.service_id}`)} className="w-full flex items-center justify-between p-3 rounded-xl bg-[#0B101D] border border-white/5 hover:border-[#E5C378]/30 text-left">
                  <div>
                    <div className="text-sm font-medium text-[#F7F5F0]">{a.title}</div>
                    <div className="text-xs text-[#8A97AC]">{fmtDate(a.date)} · {a.time} · {a.slot}</div>
                  </div>
                  <StatusBadge status={a.status} />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent activity */}
        <div className="wf-card rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-4"><Sparkles className="w-4 h-4 text-[#E5C378]" /><h3 className="font-display font-semibold text-[#F7F5F0]">Recent activity</h3></div>
          {d.recent_activity.length === 0 ? (
            <p className="text-sm text-[#6E7D97] py-6 text-center">No recent activity yet.</p>
          ) : (
            <div className="space-y-3">
              {d.recent_activity.map((a) => (
                <div key={a.id} className="flex items-start gap-3">
                  <CheckCircle2 className="w-4 h-4 text-[#7EA293] mt-0.5 shrink-0" />
                  <div className="text-sm text-[#B8C1D1]">
                    <span className="text-[#F7F5F0] font-medium">{a.actor}</span> {a.action} <span className="text-[#E5C378]">{a.target}</span>
                    {a.detail && <span className="text-[#6E7D97]"> — {a.detail}</span>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
